import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { CommunityPost, CommunityService } from '@app/features/community';
import { LocalStorageKey } from '@app/shared/interfaces';
import { getDataWithExpiry } from '@app/shared/utils';
import { ButtonComponent, IconComponent } from '@ui';
import { Logger } from '@utils/general';
import { UiModule } from '../../../../ui/ui.module';

@Component({
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sbnb-community-summary',
  templateUrl: './community-summary.component.html',
  styleUrls: ['./community-summary.component.css'],
  imports: [ButtonComponent, IconComponent, UiModule],
})
export class CommunitySummaryComponent {
  readonly communityPosts = signal<CommunityPost[] | null>(null);
  readonly communityCollapsed = signal(false);

  private readonly communityService = inject(CommunityService);

  constructor() {
    this.loadCommunityPosts();
  }

  private loadCommunityPosts() {
    // Respect persisted collapsed state with expiry
    const persisted = getDataWithExpiry<{ collapsed: boolean }>(LocalStorageKey.communityPostsCollapsed);
    this.communityCollapsed.set(persisted?.data?.collapsed === true);

    this.communityService.getPopularPosts().subscribe({
      next: (posts) => {
        this.communityPosts.set(posts && posts.length > 0 ? posts : null);
      },
      error: (error) => {
        Logger.error('Failed to load community posts:', error);
        this.communityPosts.set(null);
      },
    });
  }

  toggleCommunityCollapse(collapsed: boolean) {
    this.communityCollapsed.set(collapsed);
    const expiresAt = this.getSevenDaysFromNowTimestamp();
    localStorage.setItem(LocalStorageKey.communityPostsCollapsed, JSON.stringify({ data: { collapsed }, expiresAt }));
  }

  openCommunityPost(post: CommunityPost): void {
    // Build UTM parameters
    const utmParams = new URLSearchParams({
      utm_source: 'myhospitable',
      utm_medium: 'referral',
      utm_campaign: 'dashboard',
      utm_content: post.id,
    });

    // Open community post in new tab with UTM parameters
    const urlWithUtm = `https://community.hospitable.com${post.url}?${utmParams.toString()}`;
    window.open(urlWithUtm, '_blank', 'noopener');
  }

  private getSevenDaysFromNowTimestamp(): number {
    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + (168 * 60 * 60 * 1000)); // 168 hours = 7 days
    return sevenDaysFromNow.getTime();
  }
}
